import { createEffect, onCleanup, mergeProps } from 'solid-js';
import { provide, consume } from 'component-register';
import {
  customShadowlessElement,
  correctElementType,
  type CorrectComponentType,
} from '@brytdesigns/web-component-core/utils';
import { getCollapsibleItemContext } from '@brytdesigns/web-component-collapsible';

import { FaqContext, useFaq, type ContextProps } from './faq-search-context';
import { FaqEmptyComponent } from './faq-search-empty';

const FaqContextElement: CorrectComponentType<ContextProps> = (props, { element }) => {
  const url = new URL(window.location.href);
  const initialCategory = url.searchParams.get('category');
  if (initialCategory && initialCategory !== props.activeCategory) {
    element.setAttribute('active-category', initialCategory);
  }

  provide(FaqContext, mergeProps(props, { element }), element);
  const [, actions] = consume(FaqContext, element)!;

  const onPopState = () => {
    const u = new URL(window.location.href);
    const cat = u.searchParams.get('category') ?? 'all';
    if (cat !== element.getAttribute('active-category')) actions.setActiveCategory(cat);
  };
  window.addEventListener('popstate', onPopState);

  const onInput = (event: Event) => {
    const target = event.target as HTMLInputElement | null;
    if (!target?.matches("input[type='search']")) return;
    actions.setQuery(target.value);
  };

  const onSubmit = (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    actions.flushQuery(String(data.get('query') ?? ''));
  };

  const onClick = (event: Event) => {
    const trigger = (event.target as HTMLElement).closest('collapsible-trigger');
    if (!trigger) return;
    const li = trigger.closest('li');
    const collapsible = trigger.closest('collapsible-item');
    if (!li || !collapsible) return;
    // The trigger's own toggle runs first (it's a descendant), so isExpanded === true
    // means the user just opened this item → collapse the rest.
    if (getCollapsibleItemContext(collapsible)?.[0].isExpanded) actions.collapseOthers(li as HTMLElement);
  };

  element.addEventListener('input', onInput);
  element.addEventListener('submit', onSubmit);
  element.addEventListener('click', onClick);
  onCleanup(() => {
    window.removeEventListener('popstate', onPopState);
    element.removeEventListener('input', onInput);
    element.removeEventListener('submit', onSubmit);
    element.removeEventListener('click', onClick);
  });
};

const FaqFilterTab: CorrectComponentType<{ handle: string }> = (props, { element }) => {
  const [state, actions] = useFaq(element);
  const button = element.querySelector('button') ?? element;
  const countEl = element.querySelector<HTMLElement>('[data-count-display]');

  createEffect(() => {
    const isActive = state.activeCategory() === props.handle;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', String(isActive));
  });

  createEffect(() => {
    if (!countEl) return;
    const count = props.handle === 'all' ? state.matchTotal() : (state.matchByGroup().get(props.handle) ?? 0);
    countEl.textContent = String(count);
  });

  const onClick = () => actions.setActiveCategory(props.handle);
  element.addEventListener('click', onClick);
  onCleanup(() => element.removeEventListener('click', onClick));
};

const FaqGroup: CorrectComponentType<{ handle: string }> = (props, { element }) => {
  const [, actions] = useFaq(element);
  actions.registerGroup(element, props.handle);
};

const FaqItem: CorrectComponentType<{ handle: string }> = (props, { element }) => {
  const [, actions] = useFaq(element);
  const liEl = element.parentElement;
  if (!liEl) return;

  const collapsible = element.querySelector<HTMLElement>('collapsible-item');
  const questionEl = element.querySelector<HTMLElement>('.faq-item__question');
  const answerEl = element.querySelector<HTMLElement>('.faq-item__answer');

  const questionText = (questionEl?.textContent ?? '').trim().toLowerCase();
  const answerText = (answerEl?.textContent ?? '').trim().toLowerCase();
  const originalQuestionHTML = questionEl?.innerHTML ?? '';
  const originalAnswerHTML = answerEl?.innerHTML ?? '';

  actions.registerItem(liEl, {
    groupHandle: props.handle,
    questionText,
    answerText,
    questionEl,
    answerEl,
    originalQuestionHTML,
    originalAnswerHTML,
    collapsible,
    autoExpanded: false,
  });
};

customShadowlessElement(
  'faq-context',
  { query: '', activeCategory: 'all', delay: 300 },
  correctElementType(FaqContextElement),
);
customShadowlessElement('faq-filter-tab', { handle: '' }, correctElementType(FaqFilterTab));
customShadowlessElement('faq-group', { handle: '' }, correctElementType(FaqGroup));
customShadowlessElement('faq-item', { handle: '' }, correctElementType(FaqItem));
customShadowlessElement('faq-empty', {}, FaqEmptyComponent);
